/**
 * Deterministic auto-description for a direct index recipe (PRD FR7).
 * Pure function of the recipe — regenerates on every recipe change unless
 * the user has taken over the description.
 */

import type {
  DirectIndexRebalancing,
  DirectIndexUniverseRefLike,
  DirectIndexWeighting,
} from "./types";

const WEIGHTING_PHRASES: Record<DirectIndexWeighting, string> = {
  index: "by index weighting",
  "market-cap": "by market cap",
  equal: "with equal weighting",
};

export function describeDirectIndex(params: {
  universe: DirectIndexUniverseRefLike;
  topN: number;
  weighting: DirectIndexWeighting;
  rebalancing: DirectIndexRebalancing;
  excludeTickers: readonly string[];
}): string {
  const subject =
    params.universe.kind === "sector"
      ? `the ${params.universe.name} sector`
      : `the ${params.universe.name}`;
  const excludes = formatExcludes(params.excludeTickers);
  return (
    `Tracks ${subject}, rebalancing ${params.rebalancing.replaceAll("-", " ")}. ` +
    `Customized to include the top ${params.topN} symbols ` +
    `${WEIGHTING_PHRASES[params.weighting]}, and exclude ${excludes}.`
  );
}

function formatExcludes(tickers: readonly string[]): string {
  if (tickers.length === 0) {
    return "no symbols";
  }
  if (tickers.length === 1) {
    return tickers[0] ?? "no symbols";
  }
  if (tickers.length === 2) {
    return `${tickers[0]} and ${tickers[1]}`;
  }
  return `${tickers.slice(0, -1).join(", ")}, and ${tickers[tickers.length - 1]}`;
}
